
import React, { useState, useEffect } from "react";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { createPageUrl, getCsrfToken } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TooltipButton } from "@/components/ui/TooltipButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WinnerBadge } from "@/components/WinnerBadge";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { UserBadge } from "@/components/UserBadge";
import { Loader2, Settings, LogOut, Music, MessageSquare, Trophy, DollarSign, Trash, X, Zap, Vote, User } from "lucide-react";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import CuratorDashboard from "@/components/CuratorDashboard";

import knifeIcon from "@/assets/icon-knife.png";
import spiderwebIcon from "@/assets/icon-spiderweb.png";

import trashFloatIcon from "@/assets/trash-float-icon.png";
import batMoonIcon from "@/assets/bat-moon.png";

// Helper component to show leaderboard badge for a user
function UserLeaderboardBadge({ userId }) {
  const { data: position } = useLeaderboardPosition(userId);
  return position ? <LeaderboardBadge position={position} /> : null;
}

// Saved Tracks List Component - uses localStorage since saved_tracks endpoint doesn't exist
function SavedTracksList({ userId }) {
  const [savedTracks, setSavedTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load saved tracks from localStorage
    try {
      const saved = localStorage.getItem(`saved_tracks_${userId}`);
      if (saved) {
        setSavedTracks(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading saved tracks:', e);
    }
    setIsLoading(false);
  }, [userId]);

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <Loader2 className="w-6 h-6 text-[#808080] mx-auto animate-spin" />
      </div>
    );
  }

  if (savedTracks.length === 0) {
    return (
      <div className="text-center py-12">
        <Music className="w-12 h-12 text-[#606060] mx-auto mb-4" />
        <p className="text-sm text-[#808080]">No saved tracks yet</p>
        <p className="text-xs text-[#707070] mt-2">Save tracks from Discover mode to see them here</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {savedTracks.map((track) => (
        <div
          key={track.id}
          className="neuro-pressed rounded-xl p-3 flex items-center justify-between"
        >
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-[#d0d0d0] truncate">
              {track.title}
            </h4>
            <p className="text-xs text-[#808080] truncate">
              {track.artist_name}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tracksToDelete, setTracksToDelete] = useState([]);
  const [selectedTrackIds, setSelectedTrackIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCurator, setIsCurator] = useState(false);

  useEffect(() => {
    document.title = "Profile - Soundope";
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: sessionResult } = await digitalOcean.auth.getSession();
      const session = sessionResult?.session;
      if (!session || !session.user) {
        console.log('[Profile] No session found');
        return;
      }

      const sessionUserId = session.user.id;
      console.log('[Profile] Loading profile for session user ID:', sessionUserId);

      const { data: profile, error: profileError } = await digitalOcean
        .from('profiles')
        .select('*')
        .eq('id', sessionUserId)
        .single();

      if (profileError) {
        console.error('[Profile] Error loading profile:', profileError);
        return;
      }

      if (!profile) {
        console.warn('[Profile] No profile found for user ID:', sessionUserId);
        return;
      }

      // Verify the profile ID matches the session user ID
      if (profile.id !== sessionUserId) {
        console.error('[Profile] Profile ID mismatch:', {
          profileId: profile.id,
          sessionUserId: sessionUserId
        });
        return;
      }

      console.log('[Profile] Loaded profile:', profile.id, profile.email);
      setCurrentUser(profile);

      // Check if user has curator role
      const { data: curatorRole } = await digitalOcean
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'curator')
        .maybeSingle();

      setIsCurator(!!curatorRole);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  // Fetch count of reviews/feedback given by the user
  const { data: reviewsGivenCount = 0 } = useQuery({
    queryKey: ['reviews-given-count', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return 0;
      
      // Count track_reviews entries (the actual reviews table)
      const { data: reviewsData } = await digitalOcean
        .from('track_reviews')
        .select('id')
        .eq('reviewer_id', currentUser.id);
      
      const reviewCount = reviewsData?.length || 0;
      console.log('[Profile] Reviews given:', reviewCount);
      return reviewCount;
    },
    enabled: !!currentUser?.id,
  });

  const { data: tracks = [], isLoading: tracksLoading } = useQuery({
    queryKey: ['user-tracks', currentUser?.id],
    queryFn: async () => {
      // Always get fresh session to ensure we have the correct user ID
      const { data: sessionResult } = await digitalOcean.auth.getSession();
      const session = sessionResult?.session;
      if (!session?.user?.id) {
        console.warn('[Profile] No session found, cannot load tracks');
        return [];
      }
      
      const sessionUserId = session.user.id;
      
      // Double-check currentUser matches session (safety check)
      if (currentUser?.id && currentUser.id !== sessionUserId) {
        console.error('[Profile] CRITICAL: User ID mismatch detected!', {
          currentUser: currentUser.id,
          sessionUserId: sessionUserId,
          currentUserEmail: currentUser.email
        });
        // Don't return tracks if there's a mismatch - this prevents cross-account contamination
        return [];
      }
      
      // Use session user ID directly (most reliable source)
      const userId = sessionUserId;
      
      console.log('[Profile] Loading tracks for user ID:', userId);
      const { data, error } = await digitalOcean
        .from('tracks')
        .select('*')
        .eq('artist_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[Profile] Error loading tracks:', error);
        return [];
      }
      
      // Verify all tracks belong to this user (additional safety check)
      const invalidTracks = data?.filter(track => track.artist_id !== userId);
      if (invalidTracks && invalidTracks.length > 0) {
        console.error('[Profile] CRITICAL: Found tracks with mismatched artist_id!', {
          userId,
          invalidTracks: invalidTracks.map(t => ({ id: t.id, artist_id: t.artist_id, title: t.title }))
        });
        // Filter out invalid tracks
        return data.filter(track => track.artist_id === userId) || [];
      }
      
      console.log('[Profile] Loaded', data?.length || 0, 'tracks for user:', userId);
      return data || [];
    },
    enabled: !!currentUser && !!currentUser.id,
    retry: false, // Prevent infinite retries on failure
    staleTime: 30000, // Cache for 30 seconds
  });

  // Fetch user badges from localStorage (user_badges endpoint doesn't exist on backend)
  const [userBadges, setUserBadges] = useState([]);
  
  useEffect(() => {
    if (currentUser?.id && !currentUser.is_anonymous) {
      try {
        const saved = localStorage.getItem(`user_badges_${currentUser.id}`);
        if (saved) {
          setUserBadges(JSON.parse(saved));
        }
      } catch (e) {
        console.error('Error loading badges:', e);
      }
    }
  }, [currentUser?.id, currentUser?.is_anonymous]);

  const calculateProfileCompletion = () => {
    if (!currentUser) return { percentage: 0, missing: [] };
    
    const checks = [
      { field: 'artist_name', label: 'Artist Name', complete: !!currentUser.artist_name },
      { field: 'bio', label: 'Bio', complete: !!currentUser.bio && currentUser.bio.length > 20 },
      { field: 'avatar_url', label: 'Profile Picture', complete: !!currentUser.avatar_url },
      { 
        field: 'social_links', 
        label: 'Social Links', 
        complete: currentUser.social_links && Object.values(currentUser.social_links).some(link => link && link.trim())
      }
    ];
    
    const completed = checks.filter(c => c.complete).length;
    const percentage = Math.round((completed / checks.length) * 100);
    const missing = checks.filter(c => !c.complete).map(c => c.label);
    
    return { percentage, missing, completed, total: checks.length };
  };

  const profileCompletion = currentUser ? calculateProfileCompletion() : { percentage: 0, missing: [] };

  const handleLogout = async () => {
    await digitalOcean.auth.signOut();
    navigate(createPageUrl("Home"));
  };

  const openDeleteModal = async () => {
    try {
      const { data: tracks } = await digitalOcean
        .from('tracks')
        .select('*')
        .eq('artist_id', currentUser.id);
      setTracksToDelete(tracks || []);
      setShowDeleteModal(true);
    } catch (error) {
      alert("Failed to load tracks. Please try again.");
    }
  };

  const handleDeleteTracks = async () => {
    if (selectedTrackIds.length === 0) {
      toast.error('No tracks selected');
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://soundope.com';
      const csrfToken = getCsrfToken();
      const authToken = localStorage.getItem('auth_token');

      const deletePromises = selectedTrackIds.map(async (trackId) => {
        const res = await fetch(`${API_URL}/api/tracks/${trackId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
          },
        });
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ error: res.statusText }));
          throw new Error(errorData.error || `Failed to delete track: ${res.status} ${res.statusText}`);
        }
        
        return res.json();
      });

      await Promise.all(deletePromises);
      
      toast.success(`Successfully deleted ${selectedTrackIds.length} track(s)`);
      setShowDeleteModal(false);
      setShowDeleteConfirm(false);
      setSelectedTrackIds([]);
      setTracksToDelete([]);
      
      // Invalidate queries to refresh the track list
      queryClient.invalidateQueries({ queryKey: ['user-tracks', currentUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['saved-tracks'] });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete tracks. Please try again.');
    }
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <p className="text-xl md:text-2xl text-[#d0d0d0] mb-4">
            Whoops, you're not signed in. No profile to see.
          </p>
          <p className="text-base md:text-lg text-[#a0a0a0]">
            <Link 
              to={createPageUrl("Auth")} 
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Sign in?
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 pb-32">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="neuro-base rounded-3xl p-8 mb-6">

              <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
                <h1 className="text-2xl md:text-3xl font-honeydripper text-[#d0d0d0]">Profile</h1>
                
                <div className="flex items-center gap-2 md:gap-4">
                  {/* Bat Moon Image - Analytics Button */}
                  <TooltipButton
                    onClick={() => navigate(createPageUrl("Analytics"))}
                    className="cursor-pointer hover:scale-105 transition-transform bg-transparent hover:bg-transparent p-0 relative"
                    variant="ghost"
                    tooltip="View your analytics and stats"
                  >
                    <div className="relative">
                      <img
                        src={batMoonIcon}
                        alt="Analytics"
                        className="w-14 h-14 md:w-32 md:h-32 object-contain"
                        style={{ filter: 'drop-shadow(0 0 12px rgba(168, 85, 247, 0.6))' }}
                      />
                      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#d0d0d0] font-honeydripper text-[8px] md:text-sm tracking-wide pointer-events-none" style={{ textShadow: '0 0 8px rgba(0,0,0,0.9), 0 0 16px rgba(0,0,0,0.7)' }}>
                        Analytics
                      </span>
                    </div>
                  </TooltipButton>
                  
                  <TooltipButton
                    onClick={openDeleteModal}
                    className="neuro-base active:neuro-pressed rounded-xl md:rounded-2xl px-3 py-2 md:px-12 md:py-8 flex items-center justify-center"
                    tooltip="Delete your tracks"
                  >
                    <img
                      src={knifeIcon}
                      alt="Delete Tracks"
                      className="w-8 h-8 md:w-16 md:h-16 object-contain"
                    />
                  </TooltipButton>
                  <TooltipButton
                    onClick={() => navigate(createPageUrl("Settings"))}
                    className="neuro-base active:neuro-pressed rounded-xl md:rounded-2xl px-3 py-2 md:px-12 md:py-8 flex items-center justify-center"
                    tooltip="Edit your profile settings"
                  >
                    <img
                      src={spiderwebIcon}
                      alt="Settings"
                      className="w-8 h-8 md:w-16 md:h-16 object-contain"
                    />
                  </TooltipButton>
                </div>
              </div>

          {profileCompletion.percentage < 100 && (
            <div className="neuro-base rounded-3xl p-6 mb-6 border-2 border-[#2a2a2a]">
              <div className="flex items-start gap-4">
                <div className="neuro-pressed w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-light text-[#a0a0a0]">{profileCompletion.percentage}%</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-honeydripper text-[#d0d0d0] mb-2">Complete Your Profile</h3>
                  <p className="text-sm font-halleyvetica text-[#808080] mb-3">
                    A complete profile helps you connect with other artists and get more engagement
                  </p>
                  
                  <div className="w-full h-2 bg-[#0a0a0a] rounded-full overflow-hidden neuro-pressed mb-3">
                    <div 
                      className="h-full bg-gradient-to-r from-[#606060] to-[#909090] transition-all duration-500"
                      style={{ width: `${profileCompletion.percentage}%` }}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {profileCompletion.missing.map(item => (
                      <span key={item} className="text-xs px-3 py-1 rounded-full bg-[#1a1a1a] text-[#808080]">
                        Missing: {item}
                      </span>
                    ))}
                  </div>

                  <TooltipButton
                    onClick={() => navigate(createPageUrl("Settings"))}
                    className="neuro-base active:neuro-pressed rounded-2xl px-6 py-2"
                    tooltip="Go to settings to complete your profile"
                  >
                    <Settings className="w-4 h-4 text-[#a0a0a0] mr-2" />
                    <span className="text-[#a0a0a0]">Complete Profile</span>
                  </TooltipButton>
                </div>
              </div>
            </div>
          )}

          {showDeleteModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] p-4 overflow-y-auto">
              <div className="neuro-base rounded-3xl p-6 max-w-2xl w-full my-8">
                <h2 className="text-xl font-light text-[#d0d0d0] mb-4 text-center">Delete Tracks</h2>
                <p className="text-sm text-[#808080] mb-6 text-center">
                  Select tracks to delete permanently
                </p>
                
                {tracksToDelete.length === 0 ? (
                  <p className="text-sm text-[#707070] text-center py-8">No tracks to delete</p>
                ) : (
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {tracksToDelete.map((track) => (
                      <TooltipProvider key={track.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                if (selectedTrackIds.includes(track.id)) {
                                  setSelectedTrackIds(selectedTrackIds.filter(id => id !== track.id));
                                } else {
                                  setSelectedTrackIds([...selectedTrackIds, track.id]);
                                }
                              }}
                              className={`w-full neuro-flat rounded-2xl p-4 flex gap-3 items-center smooth-transition hover:scale-[1.01] ${
                                selectedTrackIds.includes(track.id) ? 'neuro-pressed' : ''
                              }`}
                            >
                              <img
                                src={track.cover_image_url}
                                alt={track.title}
                                className="w-16 h-16 rounded-xl object-cover"
                              />
                              <div className="flex-1 text-left">
                                <p className="text-sm font-medium text-[#d0d0d0] truncate">
                                  {track.title}
                                </p>
                                <p className="text-xs text-[#707070]">
                                  {track.total_listens} listens • {track.total_votes} votes
                                </p>
                              </div>
                              {selectedTrackIds.includes(track.id) && (
                                <div className="w-6 h-6 rounded-full bg-[#a08080] flex items-center justify-center">
                                  <span className="text-xs text-gray-400">✓</span>
                                </div>
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{selectedTrackIds.includes(track.id) ? "Click to deselect track" : "Click to select track for deletion"}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                  </div>
                )}

                <div className="flex">
                  <Button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedTrackIds([]);
                      setTracksToDelete([]);
                    }}
                    className="flex-1 neuro-flat rounded-2xl py-3"
                    tooltip="Cancel and close delete modal"
                  >
                    <span className="text-[#808080]">Cancel</span>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Curator Dashboard */}
          {isCurator && (
            <div className="mb-6">
              <CuratorDashboard userId={currentUser.id} />
              
              {/* Curator Playlists Section */}
              <div className="neuro-base rounded-3xl p-6 mt-6">
                <h3 className="text-lg font-honeydripper text-[#d0d0d0] mb-4">My Playlists</h3>
                <div className="space-y-3">
                  {currentUser.spotify_playlist_url && (
                    <a
                      href={currentUser.spotify_playlist_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block neuro-flat rounded-xl p-4 hover:scale-[1.01] transition-transform"
                    >
                      <p className="text-sm text-[#a0a0a0]">Spotify Playlist</p>
                      <p className="text-xs text-[#707070] truncate mt-1">{currentUser.spotify_playlist_url}</p>
                    </a>
                  )}
                  {currentUser.apple_music_playlist_url && (
                    <a
                      href={currentUser.apple_music_playlist_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block neuro-flat rounded-xl p-4 hover:scale-[1.01] transition-transform"
                    >
                      <p className="text-sm text-[#a0a0a0]">Apple Music Playlist</p>
                      <p className="text-xs text-[#707070] truncate mt-1">{currentUser.apple_music_playlist_url}</p>
                    </a>
                  )}
                  {currentUser.soundcloud_playlist_url && (
                    <a
                      href={currentUser.soundcloud_playlist_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block neuro-flat rounded-xl p-4 hover:scale-[1.01] transition-transform"
                    >
                      <p className="text-sm text-[#a0a0a0]">SoundCloud Playlist</p>
                      <p className="text-xs text-[#707070] truncate mt-1">{currentUser.soundcloud_playlist_url}</p>
                    </a>
                  )}
                  {!currentUser.spotify_playlist_url && !currentUser.apple_music_playlist_url && !currentUser.soundcloud_playlist_url && (
                    <p className="text-sm text-[#707070] text-center py-4">No playlists added yet</p>
                  )}
                </div>
                <p className="text-xs text-[#606060] mt-4 text-center">
                  {currentUser.curator_verified ? (
                    <span className="text-green-500">✓ Verified Curator</span>
                  ) : (
                    <>Not verified yet. Email <a href="mailto:support@soundope.com" className="text-[#a0a0a0] underline">support@soundope.com</a> to get verified.</>
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="neuro-base rounded-3xl p-6 mb-6">
            <div className="flex items-start gap-6 mb-6">
              <div className="flex flex-col items-center gap-3">
                {currentUser.avatar_url ? (
                  <img
                    src={currentUser.avatar_url}
                    alt={currentUser.artist_name || currentUser.full_name}
                    className="w-32 h-32 rounded-3xl object-cover neuro-pressed"
                    fetchpriority="high"
                    decoding="async"
                    width="128"
                    height="128"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-3xl neuro-pressed flex items-center justify-center">
                    <User className="w-16 h-16 text-[#707070]" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <h2 className="text-2xl font-honeydripper text-[#d0d0d0] mb-3 flex items-center gap-1.5">
                  {currentUser.artist_name || currentUser.full_name}
                  {currentUser.has_winner_badge && <WinnerBadge />}
                  <UserLeaderboardBadge userId={currentUser.id} />
                </h2>
                
                <div className="flex flex-wrap gap-3 mb-3">
                  <div className="neuro-pressed rounded-xl px-3 py-2 flex items-center gap-2">
                    <Vote className="w-4 h-4 text-[#808080]" />
                    <span className="text-xs text-[#a0a0a0]">{currentUser.weekly_votes_remaining || 0} votes</span>
                  </div>
                  <div className="neuro-pressed rounded-xl px-3 py-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#b0a090]" />
                    <span className="text-xs text-[#b0a090]">{currentUser.premium_credits || 0} premium</span>
                  </div>
                </div>
                
                {currentUser.bio && (
                  <p className="text-sm font-halleyvetica text-[#909090]">{currentUser.bio}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate(createPageUrl("MyTracks"))}
                      className="neuro-pressed rounded-2xl p-4 text-center smooth-transition hover:scale-[1.02]"
                    >
                      <Music className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                      <p className="text-2xl font-light text-[#b0b0b0]">{tracks.length || 0}</p>
                      <p className="text-xs text-[#707070]">Tracks</p>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View all your uploaded tracks</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate(createPageUrl("MyReviews"))}
                      className="neuro-pressed rounded-2xl p-4 text-center smooth-transition hover:scale-[1.02]"
                    >
                      <MessageSquare className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                      <p className="text-2xl font-light text-[#b0b0b0]">{reviewsGivenCount}</p>
                      <p className="text-xs text-[#707070]">Reviews Given</p>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View all feedback you've given</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div className="neuro-pressed rounded-2xl p-4 text-center">
                <Vote className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                <p className="text-2xl font-light text-[#b0b0b0]">{currentUser.weekly_votes_remaining || 0}</p>
                <p className="text-xs text-[#707070]">Votes Left</p>
              </div>
            </div>
          </div>


          <div className="neuro-base rounded-3xl p-6">
            <h3 className="text-lg font-honeydripper text-[#d0d0d0] mb-6">My Tracks</h3>
            
            {tracks.length === 0 ? (
              <div className="text-center py-12">
                <Music className="w-12 h-12 text-[#606060] mx-auto mb-4" />
                <p className="text-sm text-[#808080] mb-6">No tracks uploaded yet</p>
                <Button
                  onClick={() => navigate(createPageUrl("Upload"))}
                  className="neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
                  tooltip="Upload your first track to Soundope"
                >
                  <span className="text-[#a0a0a0]">Upload Your First Track</span>
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {tracks.map((track) => (
                  <TooltipProvider key={track.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => navigate(createPageUrl("MyTracks"))}
                          className="neuro-flat rounded-2xl p-4 smooth-transition hover:scale-[1.02] text-left"
                        >
                    <div className="flex flex-col">
                      <img
                        src={track.cover_image_url || getDefaultCoverImage(track.id)}
                        alt={track.title}
                        className="w-full h-48 object-cover rounded-t-2xl"
                        loading="lazy"
                        width="300"
                        height="192"
                      />
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-[#d0d0d0] mb-1 truncate">
                          {track.title}
                        </h4>
                        <div className="flex gap-4 text-xs text-[#707070] mb-2">
                          <span>{track.total_listens} listens</span>
                          <span>{track.total_votes} votes</span>
                        </div>
                        <div className="flex gap-2">
                          {track.genres?.slice(0, 2).map(genre => (
                            <span
                              key={genre}
                              className="text-[15px] px-2 py-1 rounded-full bg-[#1a1a1a] text-[#808080]"
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View track details and manage this track</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            )}
        </div>

      </div>

      {showDeleteModal && (
        <>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectedTrackIds.length === 0) {
                      toast.error('Please select at least one track to delete');
                      return;
                    }
                    setShowDeleteConfirm(true);
                  }}
                  disabled={selectedTrackIds.length === 0}
                  className={`fixed bottom-40 left-1/2 -translate-x-1/2 z-[10001] rounded-full p-3 neuro-base active:neuro-pressed border border-[#2a2a2a] smooth-transition hover:scale-105 ${
                    selectedTrackIds.length === 0 ? 'opacity-40 cursor-not-allowed' : 'opacity-100 cursor-pointer'
                  }`}
                  style={{ pointerEvents: selectedTrackIds.length === 0 ? 'none' : 'auto' }}
                >
                  <img
                    src={trashFloatIcon}
                    alt="Delete selected tracks"
                    className="w-10 h-10 object-contain pointer-events-none"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{selectedTrackIds.length === 0 ? "Select tracks to delete first" : `Delete ${selectedTrackIds.length} selected track(s)`}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-center">you sure?</AlertDialogTitle>
                <AlertDialogDescription className="text-center">
                  This will permanently delete the selected track
                  {selectedTrackIds.length === 1 ? '' : 's'}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:justify-center">
                <AlertDialogCancel className="neuro-flat rounded-2xl px-6 py-2">
                  nah
                </AlertDialogCancel>
                <AlertDialogAction
                  className="neuro-base active:neuro-pressed rounded-2xl px-6 py-2"
                  onClick={handleDeleteTracks}
                >
                  fs
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  </div>
);
}
